import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Bell, Heart, Minus, Plus, Trash2 } from "lucide-react";
import { submitSubsititutionData } from "../../redux/actions/action-creator";
import "./OutOfStockAlternativePopup.css";

const OUT_OF_STOCK_KEYS = [
  "cart_product",
  "cart_products",
  "cartProduct",
  "cartProducts",
  "cart_product_detail",
  "cartProductDetail",
  "ordered_product",
  "orderedProduct",
  "original_product",
  "originalProduct",
  "out_of_stock_items",
  "out_of_stock_products",
  "outofstock_items",
  "outofstock_products",
  "stock_out_items",
  "stockout_items",
  "unavailable_items",
  "not_available_items",
  "pending_items",
  "out_of_stock_item",
  "out_of_stock_product",
  "out_of_stock_product_detail",
  "outofstock_item",
  "stock_out_item",
  "unavailable_item",
  "not_available_item",
];

const ALTERNATIVE_KEYS = [
  "alternative_suggestions",
  "alternative_items",
  "alternative_products",
  "alternative_product",
  "alternatives",
  "suggestions",
  "substitute_items",
  "substitute_products",
  "substitute_product",
  "substitute_product_details",
  "substituteProduct",
  "substituteProducts",
  "substituteProductDetails",
  "subsitute_items",
  "substitution_items",
  "substitution_products",
  "substitution_product",
  "subsitution_items",
  "substitutes",
  "subsitutes",
  "replacement_items",
  "replacement_products",
  "recommended_products",
];

const SINGLE_PRODUCT_KEYS = [
  "product",
  "item",
  "order_item",
  "product_detail",
];

const GROUP_KEYS = [
  "substitutions",
  "substitution",
  "substitution_details",
  "substitution_data",
  "details",
  "groups",
  "items_data",
];

const CART_PRODUCT_VALUE_KEYS = [
  "cart_product_id",
  "cartProductId",
  "cart_product_name",
  "cartProductName",
  "cart_product_title",
  "cartProductTitle",
  "cart_product_image",
  "cartProductImage",
  "cart_product_price",
  "cartProductPrice",
  "cart_product_variant_id",
  "cartProductVariantId",
];

const SUBSTITUTIONS_KEYS = ["substitutions"];

const ORDER_DETAIL_ID_KEYS = [
  "order_detail_id",
  "orderDetailId",
  "booking_order_detail_id",
  "bookingOrderDetailId",
  "order_item_id",
  "orderItemId",
  "cart_detail_id",
  "cartDetailId",
  "cart_item_id",
  "cartItemId",
  "id",
];

const PRODUCT_ID_KEYS = [
  "product_id",
  "productId",
  "substitute_product_id",
  "substituteProductId",
  "substitution_product_id",
  "substitutionProductId",
  "product_variant_id",
  "productVariantId",
  "variant_id",
  "variantId",
  "id",
];

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const isSuccessResponse = (response) =>
  response?.result === true || response?.result === 1 || response?.result === "1";

const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const getValue = (source, keys, fallback = "") => {
  if (!isPlainObject(source)) return fallback;

  for (const key of keys) {
    const value = source?.[key];
    if (hasValue(value)) return value;
  }

  return fallback;
};

const getValueFromSources = (sources, keys, fallback = "") => {
  for (const source of sources) {
    const parsedSource = parseJsonValue(source);
    const value = getValue(parsedSource, keys);

    if (hasValue(value)) return value;
  }

  return fallback;
};

const hasAnyValue = (source, keys) =>
  keys.some((key) => hasValue(source?.[key]));

const normalizeToArray = (value) => {
  const parsedValue = parseJsonValue(value);

  if (Array.isArray(parsedValue)) return parsedValue;
  if (isPlainObject(parsedValue)) return [parsedValue];

  return [];
};

const getCollectionByKeys = (source, keys) => {
  const parsedSource = parseJsonValue(source);
  if (!isPlainObject(parsedSource)) return [];

  for (const key of keys) {
    if (parsedSource?.[key] === undefined) continue;

    const collection = normalizeToArray(parsedSource[key]);
    if (collection.length) return collection;
  }

  return [];
};

const getNestedSources = (source) => {
  const parsedSource = parseJsonValue(source);
  if (!isPlainObject(parsedSource)) return [];

  return [
    parsedSource,
    parsedSource?.data,
    parsedSource?.response_data,
    parsedSource?.response,
    parsedSource?.substitution_details,
    parsedSource?.substitution_data,
    parsedSource?.details,
    parsedSource?.product_data,
  ]
    .map(parseJsonValue)
    .filter((item) => item && (Array.isArray(item) || isPlainObject(item)));
};

const createSection = (outOfStockItems = [], alternatives = [], parentItem = null) => ({
  outOfStockItems: outOfStockItems.filter(Boolean),
  alternatives: alternatives.filter(Boolean),
  parentItem,
});

const getProductSources = (item) => [
  item,
  item?.cart_product,
  item?.cartProduct,
  item?.cart_product_detail,
  item?.cartProductDetail,
  item?.ordered_product,
  item?.orderedProduct,
  item?.original_product,
  item?.originalProduct,
  item?.product,
  item?.variant,
];

const getCartProducts = (item) => {
  const cartProducts = getCollectionByKeys(item, OUT_OF_STOCK_KEYS);

  if (cartProducts.length) return cartProducts;
  if (isPlainObject(item?.cart_product)) return [item.cart_product];
  if (isPlainObject(item?.cartProduct)) return [item.cartProduct];
  if (hasAnyValue(item, CART_PRODUCT_VALUE_KEYS)) return [item];

  return [];
};

const normalizeSubstitutionSections = (items = []) => {
  if (!items.length) return [];

  const groupedSections = items
    .map((item) => {
      const cartProducts = getCartProducts(item);
      const directAlternatives = getCollectionByKeys(item, ALTERNATIVE_KEYS);
      const nestedAlternatives = cartProducts.flatMap((product) =>
        getCollectionByKeys(product, ALTERNATIVE_KEYS)
      );
      const alternatives = directAlternatives.length ? directAlternatives : nestedAlternatives;
      const outOfStockItems = cartProducts.length ? cartProducts : alternatives.length ? [item] : [];

      if (!outOfStockItems.length && !alternatives.length) return null;

      return createSection(outOfStockItems, alternatives, item);
    })
    .filter((section) => section?.outOfStockItems.length || section?.alternatives.length);

  if (groupedSections.length) return groupedSections;

  return [createSection([], items)];
};

const normalizeArraySections = (items = []) => {
  if (!items.length) return [];

  const groupedSections = items
    .map((item) => {
      const outOfStockItems = getCollectionByKeys(item, OUT_OF_STOCK_KEYS);
      const alternatives = getCollectionByKeys(item, ALTERNATIVE_KEYS);
      const singleProduct = getCollectionByKeys(item, SINGLE_PRODUCT_KEYS);

      if (!outOfStockItems.length && !alternatives.length && !singleProduct.length) {
        return null;
      }

      return createSection(
        outOfStockItems.length ? outOfStockItems : singleProduct.length ? singleProduct : [item],
        alternatives
      );
    })
    .filter((section) => section?.outOfStockItems.length || section?.alternatives.length);

  if (groupedSections.some((section) => section.alternatives.length)) {
    return groupedSections;
  }

  return [createSection(items, [])];
};

const normalizeSections = (response) => {
  const parsedResponse = parseJsonValue(response);

  if (!parsedResponse) return [];
  if (isPlainObject(parsedResponse) && "result" in parsedResponse && !isSuccessResponse(parsedResponse)) {
    return [];
  }

  const sources = Array.isArray(parsedResponse)
    ? [parsedResponse]
    : getNestedSources(parsedResponse);

  for (const source of sources) {
    if (Array.isArray(source)) {
      const sections = normalizeArraySections(source);
      if (sections.length) return sections;
      continue;
    }

    const substitutions = getCollectionByKeys(source, SUBSTITUTIONS_KEYS);
    if (substitutions.length) {
      const sections = normalizeSubstitutionSections(substitutions);
      if (sections.length) return sections;
    }

    const groupItems = getCollectionByKeys(source, GROUP_KEYS);
    if (groupItems.length) {
      const sections = normalizeSubstitutionSections(groupItems);
      if (sections.length) return sections;
    }

    const outOfStockItems = getCollectionByKeys(source, OUT_OF_STOCK_KEYS);
    const alternatives = getCollectionByKeys(source, ALTERNATIVE_KEYS);

    if (outOfStockItems.length || alternatives.length) {
      const sectionsWithNestedAlternatives = outOfStockItems
        .map((item) => createSection([item], getCollectionByKeys(item, ALTERNATIVE_KEYS)))
        .filter((section) => section.alternatives.length);

      if (sectionsWithNestedAlternatives.length) return sectionsWithNestedAlternatives;

      return [createSection(outOfStockItems, alternatives)];
    }

    const singleProduct = getCollectionByKeys(source, SINGLE_PRODUCT_KEYS);
    if (singleProduct.length) {
      return [createSection(singleProduct, alternatives)];
    }
  }

  return [];
};

const getItemTitle = (item) =>
  getValueFromSources(getProductSources(item), [
    "title",
    "name",
    "product_name",
    "productName",
    "product_title",
    "productTitle",
    "cart_product_name",
    "cartProductName",
    "cart_product_title",
    "cartProductTitle",
    "substitute_product_name",
    "substituteProductName",
    "substitute_product_title",
    "substituteProductTitle",
    "item_name",
    "itemName",
    "variant_name",
    "variantName",
  ], "Product");

const getItemImage = (item) =>
  getValueFromSources(getProductSources(item), [
    "image",
    "product_image",
    "productImage",
    "product_cover_image",
    "productCoverImage",
    "cart_product_image",
    "cartProductImage",
    "cart_product_cover_image",
    "cartProductCoverImage",
    "substitute_product_image",
    "substituteProductImage",
    "substitute_product_cover_image",
    "substituteProductCoverImage",
    "image_url",
    "imageUrl",
    "thumbnail",
    "cover_image",
    "coverImage",
  ]);

const getItemMeta = (item) => {
  const sources = getProductSources(item);
  const pieces = [
    getValueFromSources(sources, ["brand_name", "brand", "category_name"]),
    getValueFromSources(sources, [
      "product_details",
      "productDetails",
      "cart_product_details",
      "cartProductDetails",
      "substitute_product_details",
      "substituteProductDetails",
      "variant_details",
      "variantDetails",
      "display_quantity",
      "displayQuantity",
      "quantity_text",
      "quantityText",
      "weight_unit",
      "weightUnit",
      "cart_product_weight_unit",
      "cartProductWeightUnit",
      "substitute_product_weight_unit",
      "substituteProductWeightUnit",
      "pack_size",
      "packSize",
      "measurement",
      "weight",
      "unit",
      "size",
      "portion",
      "variant_title",
      "variantTitle",
    ]),
  ].filter(Boolean);

  const quantity = getValueFromSources(sources, [
    "quantity",
    "qty",
    "product_quantity",
    "productQuantity",
    "cart_product_quantity",
    "cartProductQuantity",
    "substitute_product_quantity",
    "substituteProductQuantity",
    "order_quantity",
    "orderQuantity",
    "item_quantity",
    "itemQuantity",
  ]);

  if (quantity && !pieces.some((piece) => String(piece).includes(String(quantity)))) {
    pieces.push(`Qty: ${quantity}`);
  }

  return [...new Set(pieces.map(String))].join(" · ");
};

const getItemPrice = (item) =>
  getValueFromSources(getProductSources(item), [
    "product_price",
    "productPrice",
    "cart_product_price",
    "cartProductPrice",
    "substitute_product_price",
    "substituteProductPrice",
    "price",
    "selling_price",
    "sellingPrice",
    "final_price",
    "finalPrice",
    "amount",
    "total_amount",
    "totalAmount",
  ]);

const getOrderDetailId = (section, fallback = "") =>
  getValueFromSources([
    section?.parentItem,
    ...(section?.outOfStockItems || []),
  ], ORDER_DETAIL_ID_KEYS, fallback);

const getProductId = (item, fallback = "") =>
  getValueFromSources(getProductSources(item), PRODUCT_ID_KEYS, fallback);

const getItemCurrency = (item, fallbackCurrency) =>
  getValueFromSources(getProductSources(item), [
    "currency",
    "currency_symbol",
    "currencySymbol",
  ], fallbackCurrency);

const getOriginalPrice = (item) =>
  getValueFromSources(getProductSources(item), [
    "original_price",
    "originalPrice",
    "cart_product_original_price",
    "cartProductOriginalPrice",
    "substitute_product_original_price",
    "substituteProductOriginalPrice",
    "mrp",
    "market_price",
    "marketPrice",
    "compare_at_price",
    "compareAtPrice",
    "retail_price",
    "retailPrice",
  ]);

const getSavings = (item) =>
  getValueFromSources(getProductSources(item), [
    "savings",
    "saving",
    "discount",
    "discount_amount",
    "discountAmount",
  ]);

const getRating = (item) =>
  getValueFromSources(getProductSources(item), [
    "avg_rating",
    "avgRating",
    "rating",
    "product_rating",
    "productRating",
  ], "4.5");

const getDefaultAlternativeQuantity = () => 0;

const getMaxQuantity = (item) => {
  const maxQuantity = getValueFromSources(getProductSources(item), [
    "max_quantity",
    "maxQuantity",
    "max_qty",
    "maxQty",
    "maximum_quantity",
    "maximumQuantity",
    "available_quantity",
    "availableQuantity",
    "stock_quantity",
    "stockQuantity",
  ], 2);
  const parsedMaxQuantity = Number(maxQuantity);

  return Number.isFinite(parsedMaxQuantity) && parsedMaxQuantity > 0 ? parsedMaxQuantity : 2;
};

const toPayloadNumber = (value) => {
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : value;
};

const getQuantityKey = (sectionIndex, itemIndex, item) =>
  `${sectionIndex}-${itemIndex}-${getProductId(item, itemIndex)}`;

const getItemStatus = (item) => {
  const status = getValueFromSources([item], ["status_label", "stock_status", "availability"]);

  if (typeof status === "string") {
    const normalizedStatus = status.toLowerCase();

    if (!["0", "1", "true", "false"].includes(normalizedStatus)) {
      return status;
    }
  }

  return "Out of Stock";
};

const formatMoney = (value, currency) => {
  if (!hasValue(value)) return "";

  const textValue = String(value);
  return /[a-zA-Z₹$€£]/.test(textValue) ? textValue : `${currency} ${textValue}`;
};

const formatNumber = (value, fallback = "0.00") => {
  if (!hasValue(value)) return fallback;

  const numberValue = Number(value);
  if (Number.isFinite(numberValue)) return numberValue.toFixed(2);

  return String(value);
};

const getOrderTitle = (data, ordersDetail) => {
  const parsedData = parseJsonValue(data);
  const dataBody = parseJsonValue(parsedData?.data);
  const orderNo = getValueFromSources([dataBody, parsedData, ordersDetail], [
    "order_no",
    "order_number",
    "order_id",
    "booking_order_id",
  ]);

  return orderNo ? `Order #${orderNo}` : "Order Alternatives";
};

const ProductImage = ({ item, outOfStock = false }) => {
  const image = getItemImage(item);
  const title = getItemTitle(item);
  const rating = getRating(item);
  const stockLabel = getItemStatus(item);

  return (
    <div className="alt-product-media">
      <div className={`alt-product-image ${image ? "has-image" : ""}`}>
        {image && (
          <img
            src={image}
            alt={title}
            onError={(event) => {
              event.currentTarget.style.display = "none";
              event.currentTarget.parentElement?.classList.remove("has-image");
            }}
          />
        )}
        <span className="img-fallback">{title.charAt(0).toUpperCase()}</span>
        <Heart className="alt-heart" size={16} fill="currentColor" strokeWidth={0} />
        {outOfStock && <span className="alt-stock-label">{stockLabel}</span>}
      </div>
      <span className="alt-rating-pill">
        {rating} <span className="alt-rating-star">★</span>
      </span>
    </div>
  );
};

const ProductRow = ({
  item,
  currency,
  type,
  quantity = 0,
  onDecrease,
  onIncrease,
}) => {
  const isOutOfStock = type === "out-stock";
  const maxQuantity = getMaxQuantity(item);
  const title = getItemTitle(item);
  const meta = getItemMeta(item);
  const price = getItemPrice(item);
  const originalPrice = getOriginalPrice(item);
  const savings = getSavings(item);
  const itemCurrency = getItemCurrency(item, currency);
  const formattedPrice = formatMoney(price, itemCurrency);

  return (
    <div className={`alt-product-card ${isOutOfStock ? "out-stock" : ""}`}>
      <ProductImage item={item} outOfStock={isOutOfStock} />
      <div className="alt-product-info">
        <p className="alt-product-name">{title}</p>
        {meta && <p className="alt-product-meta">{meta}</p>}
        {formattedPrice && <p className="alt-product-price">{formattedPrice}</p>}
        <p className="alt-product-subline">
          {hasValue(originalPrice) && (
            <span>Original Price: <b>{formatNumber(originalPrice)}</b></span>
          )}
          <span>Savings: <b>{formatNumber(savings)}</b></span>
        </p>
      </div>

      {isOutOfStock ? (
        <div className="alt-product-actions">
          <button className="alt-delete-btn" type="button" aria-label="Remove item">
            <Trash2 size={16} />
          </button>
          <button className="alt-notify-btn" type="button">
            <Bell size={12} fill="currentColor" />
            Notify
          </button>
        </div>
      ) : (
        <div className="alt-qty-panel">
          <span className="alt-max-text">Max: {maxQuantity}</span>
          <div className="alt-qty-control">
            <button
              className="alt-qty-btn"
              type="button"
              onClick={onDecrease}
              disabled={quantity <= 0}
              aria-label="Decrease quantity"
            >
              <Minus size={12} />
            </button>
            <span className="alt-qty-value">{quantity}</span>
            <button
              className="alt-qty-btn"
              type="button"
              onClick={onIncrease}
              disabled={quantity >= maxQuantity}
              aria-label="Increase quantity"
            >
              <Plus size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const OutOfStockAlternativePopup = ({
  show,
  onClose,
  data,
  ordersDetail = {},
  orderId,
  merchantId,
  loading = false,
  error = "",
}) => {
  const dispatch = useDispatch();
  const sections = useMemo(() => normalizeSections(data), [data]);
  const parsedData = parseJsonValue(data);
  const dataBody = parseJsonValue(parsedData?.data);
  const currency = getValueFromSources([dataBody, parsedData, ordersDetail], [
    "currency",
    "currency_symbol",
    "currencySymbol",
  ], "₹");
  const [quantities, setQuantities] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (show) {
      setSubmitError("");
      setSubmitting(false);
    }
  }, [show, data]);

  useEffect(() => {
    setQuantities((previousQuantities) => {
      const nextQuantities = {};

      sections.forEach((section, sectionIndex) => {
        section.alternatives.forEach((item, itemIndex) => {
          const key = getQuantityKey(sectionIndex, itemIndex, item);
          const maxQuantity = getMaxQuantity(item);
          const previousQuantity = previousQuantities[key];
          const initialQuantity = hasValue(previousQuantity)
            ? previousQuantity
            : getDefaultAlternativeQuantity();

          nextQuantities[key] = Math.min(Math.max(Number(initialQuantity) || 0, 0), maxQuantity);
        });
      });

      return nextQuantities;
    });
  }, [sections]);

  const updateQuantity = (sectionIndex, itemIndex, item, direction) => {
    const key = getQuantityKey(sectionIndex, itemIndex, item);
    const maxQuantity = getMaxQuantity(item);

    setQuantities((previousQuantities) => {
      const currentQuantity = Number(previousQuantities[key]) || 0;
      const nextQuantity = direction === "increase"
        ? Math.min(maxQuantity, currentQuantity + 1)
        : Math.max(0, currentQuantity - 1);

      return {
        ...previousQuantities,
        [key]: nextQuantity,
      };
    });
  };

  const buildProductArray = () =>
    sections
      .map((section, sectionIndex) => {
        const orderDetailId = getOrderDetailId(section);
        const productSubstitutes = section.alternatives
          .map((item, itemIndex) => {
            const productId = getProductId(item);
            const key = getQuantityKey(sectionIndex, itemIndex, item);
            const quantity = Number(quantities[key]) || 0;

            if (!hasValue(productId) || quantity <= 0) return null;

            return {
              product_id: toPayloadNumber(productId),
              quantity,
            };
          })
          .filter(Boolean);

        if (!hasValue(orderDetailId) || !productSubstitutes.length) return null;

        return {
          order_detail_id: toPayloadNumber(orderDetailId),
          product_substitutes: productSubstitutes,
        };
      })
      .filter(Boolean);

  const handleSubmit = async () => {
    if (submitting || loading || error) return;

    const productArray = buildProductArray();
    if (!productArray.length) {
      setSubmitError("No substitute products selected.");
      return;
    }

    const submitOrderId = hasValue(orderId)
      ? orderId
      : getValueFromSources([ordersDetail, dataBody, parsedData], [
          "order_id",
          "orderId",
          "booking_order_id",
          "bookingOrderId",
        ]);
    const submitMerchantId = hasValue(merchantId)
      ? merchantId
      : getValueFromSources([ordersDetail, dataBody, parsedData], [
          "merchant_id",
          "merchantId",
        ], 1);

    if (!hasValue(submitOrderId)) {
      setSubmitError("Order id not found.");
      return;
    }

    const payload = {
      order_id: toPayloadNumber(submitOrderId),
      merchant_id: toPayloadNumber(submitMerchantId),
      product_array: JSON.stringify(productArray),
      "for": 2,
      substitution_status: 1,
    };

    setSubmitting(true);
    setSubmitError("");

    try {
      await dispatch(submitSubsititutionData(payload));
      onClose();
    } catch (submitApiError) {
      setSubmitError(
        submitApiError?.response?.data?.message ||
          submitApiError?.message ||
          "Unable to submit substitute products."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="alt-backdrop" onClick={onClose}></div>

      <div className="alt-popup">
        <div className="outoff-popup-header">
          <h2 className="outoff-popup-title">{getOrderTitle(data, ordersDetail)}</h2>
          <button className="outoff-popup-close" onClick={onClose} type="button" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="alt-body">
          {loading && <div className="alt-state">Loading alternative items...</div>}

          {!loading && error && <div className="alt-state error">{error}</div>}

          {!loading && !error && !sections.length && (
            <div className="alt-state">No alternative items found.</div>
          )}

          {!loading && !error && sections.map((section, sectionIndex) => (
            <div className="alt-section" key={`alt-section-${sectionIndex}`}>
              {!!section.outOfStockItems.length && (
                <>
                  <h6 className="section-title">Out of Stock Items</h6>
                  {section.outOfStockItems.map((item, itemIndex) => (
                    <ProductRow
                      key={`out-stock-${sectionIndex}-${itemIndex}`}
                      item={item}
                      currency={currency}
                      type="out-stock"
                    />
                  ))}
                </>
              )}

              {!!section.alternatives.length && (
                <>
                  <h6 className="section-title">Alternative Suggestions</h6>
                  {section.alternatives.map((item, itemIndex) => (
                    <ProductRow
                      key={`alternative-${sectionIndex}-${itemIndex}`}
                      item={item}
                      currency={currency}
                      type="alternative"
                      quantity={
                        hasValue(quantities[getQuantityKey(sectionIndex, itemIndex, item)])
                          ? quantities[getQuantityKey(sectionIndex, itemIndex, item)]
                          : getDefaultAlternativeQuantity()
                      }
                      onDecrease={() => updateQuantity(sectionIndex, itemIndex, item, "decrease")}
                      onIncrease={() => updateQuantity(sectionIndex, itemIndex, item, "increase")}
                    />
                  ))}
                </>
              )}
            </div>
          ))}
        </div>

        <div className="alt-footer">
          {submitError && <div className="alt-submit-error">{submitError}</div>}
          <button
            className="alt-submit-btn"
            type="button"
            onClick={handleSubmit}
            disabled={loading || submitting || !!error || !sections.length}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </>
  );
};

export default OutOfStockAlternativePopup;
